import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Navbar, Nav, Modal, Spinner, Alert, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  User,
  authService,
  friendshipService,
  XpLeaderboardEntry,
  loadUnreadNotificationCount,
} from '../services/api';
import LanguageSelector from './LanguageSelector';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import LoginPage from './LoginPage';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import './HomePage.css';
import './QuizList.css';
import './LeaderboardPage.css';

const LeaderboardPage: React.FC = () => {
  const { t } = useSafeTranslation();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const [items, setItems] = useState<XpLeaderboardEntry[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /** Ranking dimension: API sort + mobile column pair (desktop shows full table). Default: level. */
  const [rankView, setRankView] = useState<'level' | 'achievement' | 'pvp' | 'solo'>('level');

  const [pagination, setPagination] = useState({
    totalCount: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
  });

  const loadUser = useCallback(async () => {
    setIsLoadingUser(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUser(null);
        setUnreadNotificationCount(0);
        return;
      }
      const u = await authService.getProfile();
      setUser(u);
      setUnreadNotificationCount(await loadUnreadNotificationCount(u));
    } catch {
      localStorage.removeItem('authToken');
      setUser(null);
      setUnreadNotificationCount(0);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (user) {
      void friendshipService.getPendingRequests().then((r) => setPendingRequestsCount(r.length)).catch(() => {});
    }
  }, [user]);

  const loadLeaderboard = useCallback(async () => {
    setListLoading(true);
    setLoadError(false);
    try {
      const data = await authService.getXpLeaderboard({
        page,
        pageSize,
        sort: rankView,
      });
      setItems(data.items);
      setPagination({
        totalCount: data.totalCount,
        page: data.page,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
      });
    } catch {
      setLoadError(true);
      setItems([]);
    } finally {
      setListLoading(false);
    }
  }, [page, pageSize, rankView]);

  useEffect(() => {
    void loadLeaderboard();
  }, [loadLeaderboard]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setUnreadNotificationCount(0);
    window.location.href = '/';
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextSize: number) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const rangeStart =
    pagination.totalCount === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.page * pagination.pageSize, pagination.totalCount);

  if (isLoadingUser) {
    return (
      <div className="home-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page leaderboard-page">
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            as={Link}
            to="/"
            className="navbar-brand d-flex align-items-center"
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            {user && (
              <NotificationBell
                className="d-lg-none"
                unreadCount={unreadNotificationCount}
                onUnreadCountChange={setUnreadNotificationCount}
              />
            )}
            {user ? (
              <Navbar.Toggle aria-controls="basic-navbar-nav" />
            ) : (
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 home-navbar-login-icon"
                onClick={() => setShowLoginModal(true)}
                title={t('common.login', 'Login')}
                aria-label={t('common.login', 'Login')}
              >
                <i className="bi bi-box-arrow-in-right" aria-hidden />
              </Button>
            )}
          </div>
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
                {!user && <NavbarMusicToggle />}
              </div>
              {user ? (
                <UserNavbarMenus
                  user={user}
                  pendingRequestsCount={pendingRequestsCount}
                  unreadNotificationCount={unreadNotificationCount}
                  onUnreadNotificationCountChange={setUnreadNotificationCount}
                  onOpenFriends={() => setShowFriendsList(true)}
                  onOpenPending={() => setShowPendingRequests(true)}
                  onLogout={handleLogout}
                />
              ) : (
                <Nav.Item className="d-none d-lg-block">
                  <Button variant="outline-primary" onClick={() => setShowLoginModal(true)}>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    {t('common.login', 'Login')}
                  </Button>
                </Nav.Item>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="home-content leaderboard-page-shell">
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={9}>
            <div className="quiz-list-page leaderboard-page-content w-100">
        {/* Same vertical rhythm as QuizList: header → filter card → results row → content */}
        <Row className="mb-4">
          <Col>
            <div className="quiz-list-header">
              <h1>{t('ranking.leaderboardPageTitle', 'Highscores')}</h1>
              <p className="text-muted mb-0">
                {t('ranking.leaderboardSubtitle', 'The best players at Quizter Club!')}
              </p>
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Card className="leaderboard-filter-card">
              <Card.Body>
                <Row className="g-3 align-items-end">
                  <Col xs={12} sm={6} md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label>{t('quiz.itemsPerPage', 'Items per page')}</Form.Label>
                      <Form.Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        disabled={listLoading}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label>{t('ranking.mobileViewLabel', 'View')}</Form.Label>
                      <Form.Select
                        value={rankView}
                        onChange={(e) => {
                          const v = e.target.value;
                          const next: 'level' | 'achievement' | 'pvp' | 'solo' =
                            v === 'achievement' || v === 'pvp' || v === 'solo' ? v : 'level';
                          setRankView(next);
                          setPage(1);
                        }}
                        disabled={listLoading}
                        aria-label={t('ranking.mobileViewLabel', 'View')}
                      >
                        <option value="level">{t('ranking.mobileViewLevel', 'Level')}</option>
                        <option value="achievement">
                          {t('ranking.mobileViewAchievement', 'Achievements')}
                        </option>
                        <option value="pvp">{t('ranking.leaderboardViewPvp', 'PvP')}</option>
                        <option value="solo">{t('ranking.leaderboardViewSolo', 'Quizzes')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {loadError && (
          <Alert variant="danger" className="d-flex flex-wrap align-items-center gap-2">
            {t('ranking.loadErrorLeaderboard', 'Could not load the leaderboard.')}
            <Button variant="outline-danger" size="sm" onClick={() => void loadLeaderboard()}>
              {t('ranking.retry', 'Try again')}
            </Button>
          </Alert>
        )}

        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <p className="text-muted mb-0">
                {t('quiz.showing', 'Showing')} {rangeStart} {t('quiz.to', 'to')} {rangeEnd} {t('quiz.of', 'of')}{' '}
                {pagination.totalCount} {t('ranking.players', 'players')}
              </p>
              {pagination.totalCount > 0 && (
                <p className="text-muted mb-0">
                  {t('quiz.page', 'Page')} {pagination.page} {t('quiz.of', 'of')} {pagination.totalPages}
                </p>
              )}
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card className="leaderboard-table-card">
              <Card.Body className="p-0">
                {listLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" />
                    <span className="visually-hidden d-block mt-2">{t('ranking.loadingLeaderboard', 'Loading leaderboard…')}</span>
                  </div>
                ) : (
                  <>
                    <div className="d-none d-md-block table-responsive">
                      <Table hover className="mb-0 leaderboard-table align-middle">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">{t('ranking.position', 'Position')}</th>
                            <th scope="col">{t('ranking.player', 'Player')}</th>
                            <th scope="col" className="text-end">
                              {t('user.level', 'Level')}
                            </th>
                            <th scope="col" className="text-end">
                              XP
                            </th>
                            <th scope="col" className="text-end">
                              {t('ranking.soloCompleted', 'Quizzes')}
                            </th>
                            <th scope="col" className="text-end">
                              {t('ranking.pvpCompleted', 'PvP')}
                            </th>
                            <th scope="col" className="text-end d-none d-lg-table-cell">
                              {t('ranking.achievementsUnlocked', 'Achievements')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center text-muted py-4">
                                {t('ranking.noLeaderboardRows', 'No players to show.')}
                              </td>
                            </tr>
                          ) : (
                            items.map((row) => (
                              <tr
                                key={row.id}
                                role="button"
                                tabIndex={0}
                                className="leaderboard-table-row"
                                onClick={() => navigate(`/profile/${row.id}`)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate(`/profile/${row.id}`);
                                  }
                                }}
                              >
                                <td>
                                  <span className="fw-semibold">#{row.rank}</span>
                                </td>
                                <td>
                                  <span className={row.isVip ? 'vip-subscriber-nickname' : undefined}>
                                    {row.nickname}
                                  </span>
                                  {row.isVip && (
                                    <i className="bi bi-gem text-warning ms-1" title="VIP" aria-hidden />
                                  )}
                                </td>
                                <td className="text-end">{row.level}</td>
                                <td className="text-end">{row.experiencePoints.toLocaleString()}</td>
                                <td className="text-end">{row.soloQuizzesCompleted}</td>
                                <td className="text-end">{row.pvpMatchesCompleted}</td>
                                <td className="text-end d-none d-lg-table-cell">{row.achievementsUnlocked}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-md-none table-responsive">
                      <Table hover className="mb-0 leaderboard-table align-middle">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">{t('ranking.position', 'Position')}</th>
                            <th scope="col">{t('ranking.player', 'Player')}</th>
                            {rankView === 'level' ? (
                              <>
                                <th scope="col" className="text-end">
                                  {t('user.level', 'Level')}
                                </th>
                                <th scope="col" className="text-end">
                                  XP
                                </th>
                              </>
                            ) : rankView === 'achievement' ? (
                              <>
                                <th scope="col" className="text-end">
                                  {t('ranking.achievementsCountShort', 'Unlocked')}
                                </th>
                                <th scope="col" className="text-end">
                                  {t('ranking.achievementPtsColumn', 'Pts')}
                                </th>
                              </>
                            ) : rankView === 'pvp' ? (
                              <>
                                <th scope="col" className="text-end">
                                  {t('ranking.pvpCompleted', 'PvP')}
                                </th>
                                <th scope="col" className="text-end">
                                  {t('user.level', 'Level')}
                                </th>
                              </>
                            ) : (
                              <>
                                <th scope="col" className="text-end">
                                  {t('ranking.soloCompleted', 'Quizzes')}
                                </th>
                                <th scope="col" className="text-end">
                                  {t('user.level', 'Level')}
                                </th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center text-muted py-4">
                                {t('ranking.noLeaderboardRows', 'No players to show.')}
                              </td>
                            </tr>
                          ) : (
                            items.map((row) => {
                              const achPts = row.achievementScore ?? 0;
                              return (
                                <tr
                                  key={row.id}
                                  role="button"
                                  tabIndex={0}
                                  className="leaderboard-table-row"
                                  onClick={() => navigate(`/profile/${row.id}`)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      navigate(`/profile/${row.id}`);
                                    }
                                  }}
                                >
                                  <td>
                                    <span className="fw-semibold">#{row.rank}</span>
                                  </td>
                                  <td>
                                    <span className={row.isVip ? 'vip-subscriber-nickname' : undefined}>
                                      {row.nickname}
                                    </span>
                                    {row.isVip && (
                                      <i className="bi bi-gem text-warning ms-1" title="VIP" aria-hidden />
                                    )}
                                  </td>
                                  {rankView === 'level' ? (
                                    <>
                                      <td className="text-end">{row.level}</td>
                                      <td className="text-end">{row.experiencePoints.toLocaleString()}</td>
                                    </>
                                  ) : rankView === 'achievement' ? (
                                    <>
                                      <td className="text-end">{row.achievementsUnlocked}</td>
                                      <td className="text-end">{achPts.toLocaleString()}</td>
                                    </>
                                  ) : rankView === 'pvp' ? (
                                    <>
                                      <td className="text-end">{row.pvpMatchesCompleted}</td>
                                      <td className="text-end">{row.level}</td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="text-end">{row.soloQuizzesCompleted}</td>
                                      <td className="text-end">{row.level}</td>
                                    </>
                                  )}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {!listLoading && pagination.totalPages > 1 && (
          <Row className="mt-4">
            <Col className="d-flex justify-content-center leaderboard-pagination-bar">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  {t('common.previous', 'Previous')}
                </Button>
                <span className="small text-muted">
                  {t('ranking.pageOf', 'Page {{page}} of {{total}}')
                    .replace('{{page}}', String(pagination.page))
                    .replace('{{total}}', String(pagination.totalPages))}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  {t('common.next', 'Next')}
                </Button>
              </div>
            </Col>
          </Row>
        )}

        <div className="back-to-home-card d-flex justify-content-center">
          <Button
            variant="outline-secondary"
            size="sm"
            type="button"
            onClick={() => navigate('/')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            {t('navigation.backToHome', 'Back to Home')}
          </Button>
        </div>
            </div>
          </Col>
        </Row>
      </Container>

      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('auth.loginRequired', 'Login Required')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoginPage
            onLoginSuccess={() => {
              setShowLoginModal(false);
              void loadUser();
            }}
          />
        </Modal.Body>
      </Modal>

      {showFriendsList && <FriendsList onClose={() => setShowFriendsList(false)} />}
      {showPendingRequests && (
        <PendingRequests
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={() => {
            void friendshipService.getPendingRequests().then((r) => setPendingRequestsCount(r.length));
          }}
        />
      )}
    </div>
  );
};

export default LeaderboardPage;
